import React from 'react';

// The classic drill-down: score header, factor bars, placement history, contact.
//
// The original drew five bars — relationship_depth, contactability, relevance,
// recency, where_now_certainty. fit-v2.0 has four factors plus a multiplier, and
// two of those five were CONSTANTS across all 6,014 V0 rows. The bar visual is
// reproduced; the labels are the factors that actually exist, because a bar that
// is always full is decoration, not information.

export default function CandidateDrawerClassic({ candidate, isOpen, onClose, scoringConfig }) {
  if (!isOpen || !candidate) return null;

  const fitScore = candidate.fit_score || 0;
  const callNow = scoringConfig?.call_now ?? 60;
  const warm = scoringConfig?.warm ?? 35;

  const scoreText = fitScore >= callNow ? 'text-emerald-700' : fitScore >= warm ? 'text-amber-700' : 'text-gray-700';
  const scoreBg = fitScore >= callNow ? 'bg-emerald-100' : fitScore >= warm ? 'bg-amber-100' : 'bg-gray-100';

  const factors = candidate.factors || [];
  const placements = candidate.placements || [];

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity" onClick={onClose} />

      <aside
        className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 overflow-y-auto shadow-xl"
        data-testid="classic-drawer"
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-5 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 truncate">
              {candidate.full_name || 'Unknown Candidate'}
            </h2>
            {candidate.local_name && <p className="text-sm text-gray-500 mt-1">{candidate.local_name}</p>}
            <p className="text-sm text-gray-500 mt-1">
              {[candidate.current_title, candidate.current_company].filter(Boolean).join(' · ') || 'Unknown Title'}
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div
              className={`w-12 h-12 rounded-xl ${scoreBg} flex items-center justify-center`}
              data-testid="classic-drawer-score"
            >
              <span className={`text-base font-bold ${scoreText}`}>{fitScore}</span>
            </div>
            <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-700 text-xl leading-none">
              ×
            </button>
          </div>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* Fit Score Breakdown */}
          <section>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Fit Score Breakdown</h3>
            <div className="space-y-3" data-testid="classic-factor-bars">
              {factors.map((f) => {
                const pct = f.max > 0 ? Math.round((f.points / f.max) * 100) : 0;
                return (
                  <div key={f.name}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-600">{f.label || f.name}</span>
                      <span className="text-sm font-medium text-gray-900">
                        {f.points}/{f.max}
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div className="bg-[#1B3A6B] h-2 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}

              {/* Reachability is set apart because it is a MULTIPLIER, not a
                  factor: it scales the four above and can only ever reduce the
                  total. Drawing it as a fifth bar would imply it adds points. */}
              <div className="pt-2 mt-1 border-t border-gray-100 flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  Reachability <span className="text-gray-400">(multiplier)</span>
                </span>
                <span className="text-sm font-medium text-gray-900">
                  ×{(candidate.reachability ?? 1).toFixed(2)}
                </span>
              </div>
            </div>
          </section>

          {/* Why they rank here */}
          {(candidate.reasons || []).length > 0 && (
            <section>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Evidence</h3>
              <ul className="space-y-2" data-testid="classic-drawer-reasons">
                {candidate.reasons.map((r, i) => (
                  <li key={r.code || i} className="flex gap-2 text-sm text-gray-700">
                    <span className="text-gray-300 select-none">•</span>
                    <span>{r.text}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Placement History */}
          <section>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Placement History</h3>
            {placements.length > 0 ? (
              <ul className="space-y-3" data-testid="classic-drawer-placements">
                {placements.map((p, i) => (
                  <li key={i} className="text-sm">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-medium text-gray-900">{p.company}</span>
                      {p.is_placement && (
                        <span className="shrink-0 text-[11px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
                          placed
                        </span>
                      )}
                    </div>
                    <div className="text-gray-500">
                      {p.title || 'Role not recorded'}
                      {(p.start_date || p.end_date) && (
                        <>
                          {' '}
                          · {p.start_date || '?'} – {p.end_date || 'present'}
                        </>
                      )}
                    </div>
                    {p.hiring_manager && <div className="text-gray-500 text-xs mt-0.5">HM: {p.hiring_manager}</div>}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">No placements at companies you know.</p>
            )}
          </section>

          {/* Provenance */}
          <section>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Provenance</h3>
            <div className="flex flex-wrap gap-1.5">
              {(candidate.provenance || []).length > 0 ? (
                candidate.provenance.map((source) => (
                  <span
                    key={source}
                    className="px-2 py-0.5 bg-gray-50 border border-gray-200 rounded-full text-[11px] text-gray-600 capitalize"
                  >
                    {source === 'ats' ? 'ATS' : source}
                  </span>
                ))
              ) : (
                <span className="text-sm text-gray-500">No recorded sources.</span>
              )}
            </div>
          </section>

          {/* Contact */}
          <section>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Contact</h3>
            <div className="text-sm text-gray-700 space-y-1">
              <div>Email: {candidate.channels?.email ? 'on file' : '—'}</div>
              <div>Phone: {candidate.channels?.phone ? 'on file' : '—'}</div>
              {(candidate.city || candidate.country) && (
                <div className="text-gray-500 pt-1">
                  {[candidate.city, candidate.country].filter(Boolean).join(', ')}
                </div>
              )}
            </div>
          </section>
        </div>
      </aside>
    </>
  );
}
